<template>
  <a-layout class="layout">
    <!-- 水平布局（顶部菜单） -->
    <template v-if="settingsStore.isHorizontalLayout">
      <!-- 顶部导航 -->
      <a-layout-header class="header">
        <div class="logo">
          <img src="../assets/logo.svg" alt="Logo" class="logo-icon" />
          <span class="logo-text">找厕所管理系统</span>
        </div>
        <a-menu
          v-model:selectedKeys="selectedKeys"
          theme="light"
          mode="horizontal"
          :style="{ lineHeight: '64px' }"
        >
          <a-menu-item v-for="route in mainRoutes" :key="route.path">
            <router-link :to="{ path: '/' + route.path }">
              <component :is="icons[route.meta.icon]" />
              <span>{{ route.meta.title }}</span>
            </router-link>
          </a-menu-item>
        </a-menu>
        <div class="header-right">
          <a-tooltip title="切换到左侧菜单">
            <a-button type="text" @click="settingsStore.toggleLayoutMode()" class="layout-toggle">
              <menu-outlined />
            </a-button>
          </a-tooltip>
          <a-dropdown>
            <a class="user-dropdown" @click.prevent>
              <a-avatar>{{ userInfo.username?.charAt(0) || 'U' }}</a-avatar>
              <span class="username">{{ userInfo.username || '管理员' }}</span>
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item key="profile">
                  <router-link to="/profile">
                    <user-outlined />
                    个人设置
                  </router-link>
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout">
                  <logout-outlined />
                  退出登录
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </a-layout-header>

      <!-- 内容区 -->
      <a-layout-content class="content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </a-layout-content>

      <!-- 页脚 -->
      <a-layout-footer class="footer">
        找厕所小程序管理系统 &copy; {{ new Date().getFullYear() }}
      </a-layout-footer>
    </template>

    <!-- 垂直布局（左侧菜单） -->
    <template v-else>
      <!-- 顶部导航条 -->
      <a-layout-header class="vertical-header">
        <div class="logo">
          <img src="../assets/logo.svg" alt="Logo" class="logo-icon" />
          <span class="logo-text">找厕所管理系统</span>
        </div>
        <div class="header-right">
          <a-tooltip title="切换到顶部菜单">
            <a-button type="text" @click="settingsStore.toggleLayoutMode()" class="layout-toggle">
              <appstore-outlined />
            </a-button>
          </a-tooltip>
          <a-dropdown>
            <a class="user-dropdown" @click.prevent>
              <a-avatar>{{ userInfo.username?.charAt(0) || 'U' }}</a-avatar>
              <span class="username">{{ userInfo.username || '管理员' }}</span>
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item key="profile">
                  <router-link to="/profile">
                    <user-outlined />
                    个人设置
                  </router-link>
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout">
                  <logout-outlined />
                  退出登录
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </a-layout-header>

      <a-layout class="vertical-layout">
        <!-- 左侧菜单 -->
        <a-layout-sider 
          v-model:collapsed="settingsStore.collapsed" 
          class="sider"
          :trigger="null"
          collapsible
          width="200"
          collapsed-width="80"
        >
          <div class="trigger" @click="settingsStore.toggleSidebar()">
            <menu-fold-outlined v-if="!settingsStore.collapsed" />
            <menu-unfold-outlined v-else />
          </div>
          <a-menu
            v-model:selectedKeys="selectedKeys"
            theme="light"
            mode="inline"
            :inline-collapsed="settingsStore.collapsed"
          >
            <a-menu-item v-for="route in mainRoutes" :key="route.path">
              <router-link :to="{ path: '/' + route.path }">
                <component :is="icons[route.meta.icon]" />
                <span>{{ route.meta.title }}</span>
              </router-link>
            </a-menu-item>
          </a-menu>
        </a-layout-sider>

        <!-- 右侧内容区 -->
        <a-layout>
          <a-layout-content class="content">
            <div class="content-wrapper">
              <router-view />
            </div>
          </a-layout-content>

          <!-- 页脚 -->
          <a-layout-footer class="footer">
            找厕所小程序管理系统 &copy; {{ new Date().getFullYear() }}
          </a-layout-footer>
        </a-layout>
      </a-layout>
    </template>
  </a-layout>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore, useSettingsStore } from '../store'
import { 
  DashboardOutlined, 
  UserOutlined, 
  ScheduleOutlined, 
  SettingOutlined, 
  FundOutlined,
  LogoutOutlined,
  MenuOutlined,
  AppstoreOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'LayoutView',
  components: {
    DashboardOutlined,
    UserOutlined,
    ScheduleOutlined,
    SettingOutlined,
    FundOutlined,
    LogoutOutlined,
    MenuOutlined,
    AppstoreOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const userStore = useUserStore()
    const settingsStore = useSettingsStore()
    
    const selectedKeys = ref([])
    const userInfo = computed(() => userStore.userInfo)
    
    // 图标映射
    const icons = {
      dashboard: DashboardOutlined,
      user: UserOutlined,
      schedule: ScheduleOutlined,
      setting: SettingOutlined,
      fund: FundOutlined
    }
    
    // 获取主路由
    const mainRoutes = computed(() => {
      return router.options.routes.find(r => r.path === '/').children.filter(r => r.meta && r.meta.icon && r.path !== 'profile')
    })
    
    // 监听路由变化
    watch(
      () => route.path,
      (path) => {
        const currentPath = path.split('/')[1] || 'dashboard'
        selectedKeys.value = [currentPath]
      },
      { immediate: true }
    )
    
    // 退出登录
    const handleLogout = () => {
      userStore.logout()
      router.push('/login')
    }
    
    // 组件挂载时执行
    onMounted(() => {
      const currentPath = route.path.split('/')[1] || 'dashboard'
      selectedKeys.value = [currentPath]
    })
    
    return {
      selectedKeys,
      mainRoutes,
      userInfo,
      settingsStore,
      handleLogout,
      icons
    }
  }
}
</script>

<style scoped>
.layout {
  min-height: 100vh;
}

/* 水平布局样式 */
.header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 10;
}

/* 垂直布局样式 */
.vertical-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  padding: 0 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 10;
}

.vertical-layout {
  min-height: calc(100vh - 64px);
}

.logo {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 20px;
  font-size: 18px;
  font-weight: bold;
  white-space: nowrap;
  color: #333;
}

.logo-icon {
  width: 32px;
  height: 32px;
  margin-right: 8px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
}

.header-right {
  margin-left: auto;
  padding-right: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: all 0.3s;
}

.layout-toggle:hover {
  background-color: #f5f5f5;
}

.user-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #333;
}

.username {
  margin-left: 8px;
}

.sider {
  position: relative;
  background: #fff;
  border-right: 1px solid #f0f0f0;
}

.trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background: #f5f5f5;
  color: #333;
  cursor: pointer;
  transition: all 0.3s;
  margin: 8px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.trigger:hover {
  background: #e6f7ff;
  border-color: #40a9ff;
  color: #1890ff;
}

.content {
  padding: 24px;
  background: #f5f5f5;
}

.content-wrapper {
  background: #fff;
  padding: 24px;
  border-radius: 4px;
  min-height: calc(100vh - 64px - 70px - 48px);
}

.footer {
  text-align: center;
  color: #666;
  background: #fff;
}
</style> 